import interactionPlugin from '@fullcalendar/interaction';
import FullCalendar, {
  BusinessHoursInput,
  SlotLabelContentArg,
} from '@fullcalendar/react';
import resourceTimelinePlugin from '@fullcalendar/resource-timeline';
import {
  ScheduledEvent,
  ScheduledEventColourCoding,
} from '@payaca/types/scheduledEventsTypes';
import { User } from '@payaca/types/userTypes';
import UntitledIcon from '@payaca/untitled-icons';
import React, { FC, useMemo, type JSX } from 'react';
import UserIndicator from '../userIndicator/UserIndicator';
import Calendar from './Calendar';
import SlotLabel from './SlotLabel';
import './UserResourceCalendar.sass';

const getResources = (users: User[] | undefined, showUnassigned: boolean) => {
  const userResources = [];

  if (showUnassigned) {
    userResources.push({
      id: 'unassigned',
      title: 0,
      sortIndex: 0,
    });
  }

  userResources.push(
    ...(users
      ? users.map((u, i) => ({
          id: u.id,
          title: u.id,
          sortIndex: i + 1,
        }))
      : [])
  );

  return userResources;
};
type Props = {
  additionalCalendarProps?: any;
  onExistingEventSelect: (event: ScheduledEvent) => void;
  onDateSelect: (dateSelectInfo?: {
    beginAt: string;
    endAt?: string;
    userAssignments?: number[];
  }) => void;
  onViewableDateChange?: (dateInfo: any) => void;
  users?: User[];
  scheduledEvents: ScheduledEvent[];
  showUnassigned?: boolean;
  eventDrop?: any;
  eventResize?: any;
  onViewTypeChange?: (viewType: string) => void;
  initialView?: string;
  minTime?: string;
  maxTime?: string;
  hiddenDays?: number[];
  className?: string;
  scheduledEventColourCoding?: ScheduledEventColourCoding;
  calendarRef?: React.RefObject<FullCalendar | null>;
  isExternallyControlled?: boolean;
  businessHours?: BusinessHoursInput;
};
const UserResourceCalendar: FC<Props> = ({
  additionalCalendarProps = {},
  onExistingEventSelect,
  onDateSelect,
  onViewableDateChange,
  users,
  scheduledEvents,
  showUnassigned = true,
  eventDrop,
  eventResize,
  onViewTypeChange,
  initialView,
  minTime,
  maxTime,
  hiddenDays,
  className,
  scheduledEventColourCoding,
  calendarRef,
  isExternallyControlled,
  businessHours,
}: Props): JSX.Element => {
  const parsedEventsWithResources = useMemo(() => {
    const eventsWithResources: any[] = [];
    scheduledEvents.forEach((se) => {
      // add event for each user
      const userAssignments = se.userAssignments;
      if (userAssignments?.length) {
        userAssignments.forEach((ua) => {
          eventsWithResources.push({
            ...se,
            userAssignment: ua,
          });
        });
      } else {
        // add event with unassigned user
        eventsWithResources.push({
          ...se,
          userAssignment: 'unassigned',
        });
      }
    });
    return eventsWithResources;
  }, [scheduledEvents]);

  return (
    <Calendar
      isExternallyControlled={isExternallyControlled}
      additionalCalendarProps={{
        views: {
          day: {
            titleFormat: 'ddd, D MMM YYYY',
          },
        },
        resourceOrder: 'sortIndex',
        resources: getResources(users, showUnassigned),
        resourceAreaWidth: '15%',
        resourceAreaHeaderContent: ' ',
        resourceLabelContent: (resource: {
          fieldValue: string;
          resource: any;
          view: any;
        }) => {
          const user = users?.find((u) => u.id === +resource.fieldValue);
          if (user) {
            return (
              <div className="user-resource">
                <UserIndicator
                  user={{
                    firstName: user.firstname,
                    lastName: user.lastname,
                    emailAddress: user.email,
                    userColour: user.userColour,
                    imgSrc: user.avatarUrl,
                  }}
                  size="sm"
                />
                <span> {user.name || user.firstname}</span>
              </div>
            );
          }
          return (
            <div className="user-resource">
              <div className="unassigned-user">
                <UntitledIcon name="user-minus-01.3" className="w-4 h-4" />
              </div>
              <span>Unassigned</span>
            </div>
          );
        },
        ...additionalCalendarProps,
      }}
      className={`user-resource-calendar timeline-calendar${
        className ? ` ${className}` : ''
      }`}
      initialView={initialView}
      hasBoxShadow={false}
      headerToolbar={{
        left: 'today,jumpToDate',
        center: 'prev title next',
        right: 'resourceTimelineMonth,resourceTimelineWeek,resourceTimelineDay',
      }}
      onDateSelect={onDateSelect}
      onExistingEventSelect={onExistingEventSelect}
      onViewableDateChange={onViewableDateChange}
      plugins={[interactionPlugin, resourceTimelinePlugin]}
      scheduledEvents={parsedEventsWithResources}
      renderSlotLabelContent={(slotLabelContentArg: SlotLabelContentArg) => {
        return <SlotLabel slotLabelContentArg={slotLabelContentArg} />;
      }}
      eventDrop={eventDrop}
      eventResize={eventResize}
      onViewTypeChange={onViewTypeChange}
      minTime={minTime}
      maxTime={maxTime}
      hiddenDays={hiddenDays}
      scheduledEventColourCoding={scheduledEventColourCoding}
      externalCalendarRef={calendarRef}
      businessHours={businessHours}
    />
  );
};

export default UserResourceCalendar;
